import { FadeUp, MaskedLines } from '../components/anim';
import { BIO, ACHIEVEMENTS, CAREER, EDUCATION, LANGUAGES, EXPERTISE, TITLE, UI } from '../data/portfolio';
import { useLang } from '../i18n';

export default function About() {
  const { t, lang } = useLang();
  return (
    <main data-testid="about-page" className="px-6 pt-28 md:px-10 md:pt-40">
      <div className="mx-auto max-w-[1600px]">
        <MaskedLines
          lines={[
            <h1 key="a" data-testid="about-heading" className="text-[clamp(34px,4.2vw,60px)] font-light uppercase tracking-[0.14em] text-balance">
              {t(UI.aboutTitle)}
            </h1>,
          ]}
        />
        <div className="mt-10 h-px bg-ink/25" />

        <div className="mt-16 grid gap-12 pb-16 md:mt-24 md:grid-cols-12 md:pb-24">
          <FadeUp className="md:col-span-4">
            <div className="flex h-full flex-col">
              <div className="aspect-[3/4] max-w-sm overflow-hidden bg-paper">
                <img
                  src="/work/portrait.jpg"
                  alt="Donatien Grimaud"
                  data-testid="about-portrait"
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="mt-auto pt-4 text-[10px] uppercase tracking-[0.25em] text-ink/50">
                Donatien Grimaud — Paris
              </div>
            </div>
          </FadeUp>

          <div className="flex h-full flex-col md:col-span-7 md:col-start-6">
            <FadeUp>
              <p className="text-[10px] uppercase tracking-[0.3em] text-ink/50">{TITLE}</p>
            </FadeUp>
            <div className="mt-10 space-y-8" data-testid="about-bio">
              {BIO[lang].map((p, i) => (
                <FadeUp key={i} delay={i * 0.08}>
                  <p className="max-w-[36rem] text-[15px] leading-[1.55] text-ink/85 text-pretty md:text-[16px]">{p}</p>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.1} className="mt-auto pt-10">
              <p className="text-[10px] uppercase tracking-[0.3em] text-ink/40">
                Retail Architecture — Luxury — Beauty & Spa — Hospitality
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="h-px bg-ink/25" />

        {/* Key achievements — from the CV */}
        <div className="grid gap-12 py-16 md:grid-cols-12 md:py-24">
          <div className="hidden md:col-span-4 md:block" />
          <FadeUp delay={0.1} className="md:col-span-7 md:col-start-6">
            <h2 className="text-xs uppercase tracking-[0.3em] text-ink/60">
              {lang === 'en' ? 'Key achievements' : 'Réalisations'}
            </h2>
            <ul className="mt-8 space-y-6" data-testid="about-achievements">
              {ACHIEVEMENTS.map((a, i) => (
                <li key={i} className="flex gap-4 border-t border-ink/10 pt-6">
                  <span className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ink/50"></span>
                  <p className="max-w-xl text-[15px] leading-[1.55] text-ink/80 text-pretty md:text-[16px]">{t(a.text)}</p>
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>

        <div className="h-px bg-ink/25" />

        {/* Expertise — CV */}
        <section className="py-20 md:py-28">
          <FadeUp>
            <h2 className="text-xs uppercase tracking-[0.35em] text-ink/60 md:text-sm">{t(UI.expertise)}</h2>
          </FadeUp>
          <div className="mt-12 grid gap-x-12 gap-y-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="about-expertise">
            {EXPERTISE.map((it, i) => (
              <FadeUp key={i} delay={i * 0.04}>
                <div className="flex items-baseline gap-4 border-t border-ink/10 pt-4">
                  <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-ink/50"></span>
                  <span className="text-sm font-light tracking-wide text-ink/80">{t(it)}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        {/* Career — from the CV */}
        <section className="border-t border-ink/15 py-20 md:py-28" data-testid="about-career">
          <FadeUp>
            <h2 className="text-xs uppercase tracking-[0.35em] text-ink/60 md:text-sm">{t(UI.career)}</h2>
          </FadeUp>
          <div className="mt-12 space-y-10">
            {CAREER.map((c, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <div className="grid gap-2 border-t border-ink/10 pt-6 md:grid-cols-12 md:items-baseline">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-ink/45 md:col-span-3">{t(c.dates)}</span>
                  <span className="text-xs font-medium uppercase tracking-[0.2em] md:col-span-5">{c.house}</span>
                  <span className="text-sm font-light tracking-wide text-ink/80 md:col-span-4">{t(c.role)}</span>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        {/* Education & languages */}
        <section className="border-t border-ink/15 py-20 md:py-28">
          <div className="grid gap-14 md:grid-cols-2">
            <FadeUp>
              <h2 className="text-xs uppercase tracking-[0.35em] text-ink/60 md:text-sm">{t(UI.education)}</h2>
              <div className="mt-10 space-y-8">
                {EDUCATION.map((e, i) => (
                  <div key={i} className="border-t border-ink/10 pt-6">
                    <div className="text-xs font-medium uppercase tracking-[0.2em]">{t(e.school)}</div>
                    <div className="mt-2 text-sm font-light tracking-wide text-ink/70">{t(e.detail)}</div>
                  </div>
                ))}
              </div>
            </FadeUp>
            <FadeUp delay={0.08}>
              <h2 className="text-xs uppercase tracking-[0.35em] text-ink/60 md:text-sm">{t(UI.languages)}</h2>
              <ul className="mt-10 space-y-4">
                {LANGUAGES[lang].map((l) => (
                  <li key={l} className="border-t border-ink/10 pt-4 text-sm font-light tracking-wide text-ink/80">
                    {l}
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </section>

        {/* Download CV — discreet typographic link */}
        <section className="border-t border-ink/15 py-16 pb-28 text-center md:py-24 md:pb-36">
          <FadeUp>
            <a
              href="/donatien-grimaud-cv.pdf"
              download
              data-testid="about-download-cv"
              className="inline-block border-b border-ink/40 pb-1 text-[11px] uppercase tracking-[0.3em] transition-colors hover:border-ink"
            >
              {t(UI.downloadCV)}
            </a>
          </FadeUp>
        </section>
      </div>
    </main>
  );
}
